import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { dashboardExpVsRevData } from "../../../ApiService/ApiService";
import { BarChart } from "@mui/x-charts";

function ExpensesVsRevenue({
  schoolId,
  sessions,
  sessionsError,
  sessionsLoading,
}) {
  const [sessionId, setSessionId] = useState("");
  const [expVsRevData, setExpVsRevData] = useState(null);
  const [barDataYear, setBarDataYear] = useState({ xAxis: [], series: [] });
  const [barDataMonth, setBarDataMonth] = useState({ xAxis: [], series: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      setLoading(true);
      setError(null);
      dashboardExpVsRevData(sessionId, schoolId)
        .then((response) => {
          const data = response.data.data;
          setExpVsRevData(data);

          const yearData = data.byYear || {};
          const yearLabels = Object.keys(yearData);
          const yearRevenueData = [];
          const yearExpenseData = [];

          yearLabels.forEach((year) => {
            const yearItems = yearData[year];
            yearItems.forEach((item) => {
              if (item.name === "Revenue") {
                yearRevenueData.push(item.data);
              } else if (item.name === "Expense") {
                yearExpenseData.push(item.data);
              }
            });
          });

          setBarDataYear({
            xAxis: yearLabels,
            series: [
              {
                label: "Revenue",
                data: yearRevenueData,
                color: "green",
              },
              {
                label: "Expense",
                data: yearExpenseData,
                color: "red",
              },
            ],
          });

          // Prepare bar chart data for byMonth
          const monthData = data.byMonth || {};
          const monthLabels = Object.keys(monthData);
          const monthRevenueData = [];
          const monthExpenseData = [];

          monthLabels.forEach((month) => {
            const monthItems = monthData[month];
            monthItems.forEach((item) => {
              if (item.name === "Revenue") {
                monthRevenueData.push(item.data);
              } else if (item.name === "Exp") {
                monthExpenseData.push(item.data);
              }
            });
          });

          setBarDataMonth({
            xAxis: monthLabels,
            series: [
              {
                label: "Revenue",
                data: monthRevenueData,
                color: "green",
              },
              {
                label: "Expense",
                data: monthExpenseData,
                color: "red",
              },
            ],
          });

          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching expenses vs revenue data:", error);
          setError("Error fetching expenses vs revenue data");
          setLoading(false);
        });
    }
  }, [sessionId, schoolId]);

  useEffect(() => {
    if (sessions?.data?.length > 0) {
      setSessionId(sessions.data[0].id);
    }
  }, [sessions]);

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <FormControl required size="small" sx={{ width: "25%", marginBottom: 5 }}>
        <InputLabel id="sessions">Sessions</InputLabel>
        <Select
          name="sessionId"
          label="Sessions"
          labelId="sessions"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          size="small"
          disabled={sessionsLoading || sessionsError}
        >
          {sessionsLoading ? (
            <MenuItem value="">
              <CircularProgress size={24} />
            </MenuItem>
          ) : sessionsError ? (
            <MenuItem value="">
              <Alert severity="error">Error fetching sessions</Alert>
            </MenuItem>
          ) : (
            sessions?.data?.map((currSession) => (
              <MenuItem key={currSession.id} value={currSession.id}>
                {currSession.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : expVsRevData ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ marginBottom: 1, fontWeight: "bold" }}
              >
                Revenue Vs Expense by Year
              </Typography>
              <BarChart
                xAxis={[{ scaleType: "band", data: barDataYear.xAxis }]}
                series={barDataYear.series}
                width={300}
                height={250}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ marginBottom: 1, fontWeight: "bold" }}
              >
                Revenue Vs Expense by Month
              </Typography>
              <BarChart
                xAxis={[{ scaleType: "band", data: barDataMonth.xAxis }]}
                series={barDataMonth.series}
                width={400}
                height={250}
              />
            </Box>
          </Box>
        </>
      ) : (
        <Typography variant="body1" sx={{ marginTop: 2 }}>
          Please select a session to view expenses vs revenue data.
        </Typography>
      )}
    </Box>
  );
}

export default ExpensesVsRevenue;
